import {
  evaluateLoyaltySelection,
  isLoyaltyPromotion,
} from "./loyalty-selection";
export * from "./loyalty-selection";
import {
  AppliedPromotion,
  CheckoutData,
  ProductPromotionResult,
  Promotion,
  Service,
} from "@lemmonhq/types";
import { doesPromotionApplyToProduct } from "./promotion-utils";
import { validateAllRequirements } from "./requirements-validation";
import {
  calculateDiscountValueForItem,
  calculatePromotionApplications,
} from "./reward-calculation";
import {
  getCurrentTimeContext,
  getTimeContextForTableCheckout,
  isPromotionEligible,
} from "./time-utils";
import { getProductIdsFromCategories } from "./item-utils";

// ==========================================
// MAIN EXPORT FUNCTION
// ==========================================

/**
 * Calculate applicable promotions and their applications
 * Returns an array of AppliedPromotion objects
 */
export const calculateApplicablePromotions = (
  services: Service[],
  checkout: CheckoutData,
  allPromotions: Promotion[],
): AppliedPromotion[] => {
  console.log(
    "[PROMO] calculateApplicablePromotions called with:",
    allPromotions.length,
    "promotions",
  );

  if (!checkout.payload) {
    console.log("[PROMO] ❌ No checkout payload, returning empty");
    return [];
  }

  if (!allPromotions.length) {
    console.log("[PROMO] ❌ No promotions provided, returning empty");
    return [];
  }

  console.log("[PROMO] Checkout type:", checkout.payload.type);
  console.log("[PROMO] Service method:", checkout.serviceMethod);

  // Determine time context based on checkout type
  let timeContext;
  if (checkout.payload.type === "table") {
    // For table checkouts, we need to check each promotion individually
    // because partial checkouts need to know the product requirements
    timeContext = null; // Will be calculated per promotion
    console.log("[PROMO] Table checkout - will calculate time per promotion");
  } else {
    // For cart checkouts, use current time
    timeContext = getCurrentTimeContext();
    console.log("[PROMO] Cart time context:", timeContext);
  }

  const appliedPromotions: AppliedPromotion[] = [];

  for (const promotion of allPromotions) {
    if (
      checkout.customer?.reward_selections !== undefined &&
      isLoyaltyPromotion(promotion)
    )
      continue;
    console.log(
      `[PROMO] ========== Checking promotion: "${promotion.name}" ==========`,
    );
    console.log("[PROMO] Promotion enabled:", promotion.enabled);
    console.log("[PROMO] Promotion requirements:", promotion.requirements);
    console.log("[PROMO] Promotion reward:", promotion.reward);
    console.log("[PROMO] Promotion days:", promotion.days);

    // For table checkouts, calculate time context based on promotion requirements
    let promotionTimeContext = timeContext;
    if (checkout.payload.type === "table" && !timeContext) {
      promotionTimeContext = getTimeContextForTableCheckout(
        checkout.payload.object,
        promotion.requirements,
        (categoryIds) => getProductIdsFromCategories(services, categoryIds),
      );
      console.log(
        "[PROMO] Table time context for promotion:",
        promotionTimeContext,
      );
    }

    // Quick validation checks first
    const isEligible = isPromotionEligible(promotion, promotionTimeContext!);
    console.log("[PROMO] Time eligibility check:", isEligible);
    if (!isEligible) {
      console.log("[PROMO] ❌ Skipped - not eligible by time");
      continue;
    }

    // Skip promotions with no requirements - these should be manually selected only
    if (!promotion.requirements || promotion.requirements.length === 0) {
      console.log("[PROMO] ❌ Skipped - no requirements (manual-only)");
      continue;
    }

    // Coin redemptions are manual-only: auto-applying would silently spend a
    // customer's balance because they happened to qualify (a coffee eating the
    // coins they were saving for lunch). They surface in PromotionsModal for
    // explicit selection. See docs/plans/loyalty/redemption_wiring.md.
    if (promotion.coin_program_id) {
      console.log("[PROMO] ❌ Skipped - coin redemption (manual-only)");
      continue;
    }

    // Capped membership rewards are manual-only for the same reason as coins: a
    // use-count or budget cap is a finite allowance the cashier must choose to
    // spend, not something that auto-applies just because the member qualifies.
    // Uncapped membership rewards still auto-apply. Capped programs are
    // identified from the resolved customer's memberships (cap_count/cap_amount);
    // they surface in PromotionsModal for explicit selection.
    // See docs/plans/loyalty/phase_1_capped_reward_checkout.md.
    if (promotion.membership_program_id) {
      const cappedProgramIds = new Set(
        (checkout.customer?.memberships ?? [])
          .filter((m) => m.cap_count != null || m.cap_amount != null)
          .map((m) => m.program_id),
      );
      if (cappedProgramIds.has(promotion.membership_program_id)) {
        console.log(
          "[PROMO] ❌ Skipped - capped membership reward (manual-only)",
        );
        continue;
      }
    }

    // Check if all requirements are met
    const requirementsMet = validateAllRequirements(
      services,
      promotion.requirements,
      checkout,
    );
    console.log("[PROMO] Requirements validation result:", requirementsMet);
    if (!requirementsMet) {
      console.log("[PROMO] ❌ Skipped - requirements not met");
      continue;
    }

    // Calculate and add applications for this promotion
    console.log("[PROMO] ✅ All checks passed, calculating applications...");
    const applications = calculatePromotionApplications(
      services,
      promotion,
      checkout,
    );
    console.log(
      "[PROMO] Applications calculated:",
      applications.length,
      applications,
    );
    appliedPromotions.push(...applications);
  }

  console.log(
    "[PROMO] ========== FINAL RESULT:",
    appliedPromotions.length,
    "promotions applied ==========",
  );
  console.log("[PROMO] Applied promotions:", appliedPromotions);

  if (checkout.customer?.reward_selections !== undefined) {
    appliedPromotions.push(
      ...evaluateLoyaltySelection(
        services,
        checkout,
        allPromotions,
        checkout.customer.reward_selections,
        appliedPromotions,
      ).applications,
    );
  }
  return appliedPromotions;
};

/**
 * Calculate promotion value for manually selected promotions (including those with no requirements)
 * This function can calculate values for any promotion, regardless of requirements
 */
export const calculatePromotionValue = (
  services: Service[],
  promotion: Promotion,
  checkout: CheckoutData | null,
): number => {
  if (!checkout || !checkout.payload) {
    return 0;
  }

  // Calculate applications for this specific promotion
  const applications = calculatePromotionApplications(
    services,
    promotion,
    checkout,
  );

  // Sum up all application values
  return applications.reduce(
    (total, application) => total + application.value,
    0,
  );
};

/**
 * Get all promotions that can apply to a specific product
 */
export const getPromotionsForProduct = (
  services: Service[],
  productId: number,
  allPromotions: Promotion[],
  serviceMethod?: "takeaway" | "dine-in" | "delivery",
): Promotion[] => {
  if (!allPromotions.length) {
    return [];
  }

  const timeContext = getCurrentTimeContext();
  const applicablePromotions: Promotion[] = [];

  for (const promotion of allPromotions) {
    // Quick validation checks first
    if (!isPromotionEligible(promotion, timeContext)) {
      continue;
    }

    // Check if promotion applies to this product
    if (
      !doesPromotionApplyToProduct(
        services,
        promotion,
        productId,
        serviceMethod,
      )
    ) {
      continue;
    }

    applicablePromotions.push(promotion);
  }

  return applicablePromotions;
};

/**
 * Calculate the discounted price for a product after applying the best applicable promotion
 */
export const getProductPriceAfterDiscount = (
  services: Service[],
  productId: number,
  originalPrice: number,
  quantity: number = 1,
  allPromotions: Promotion[],
  serviceMethod?: "takeaway" | "dine-in" | "delivery",
): { finalPrice: number; discount: number; appliedPromotion?: Promotion } => {
  const applicablePromotions = getPromotionsForProduct(
    services,
    productId,
    allPromotions,
    serviceMethod,
  );

  if (applicablePromotions.length === 0) {
    return {
      finalPrice: originalPrice,
      discount: 0,
    };
  }

  let bestDiscount = 0;
  let bestPromotion: Promotion | undefined;

  // Find the promotion that gives the best discount
  for (const promotion of applicablePromotions) {
    if (promotion.reward.type === "product_discount") {
      const totalPrice = originalPrice * quantity;
      const discountValue = calculateDiscountValueForItem(
        promotion.reward,
        totalPrice,
        quantity,
      );

      if (discountValue > bestDiscount) {
        bestDiscount = discountValue;
        bestPromotion = promotion;
      }
    } else if (promotion.reward.type === "special_price") {
      const specialPrice = promotion.reward.prices[productId];
      if (specialPrice !== undefined) {
        const totalPrice = originalPrice * quantity;
        const specialPriceTotal = specialPrice * quantity;
        const discountValue = Math.max(0, totalPrice - specialPriceTotal);

        if (discountValue > bestDiscount) {
          bestDiscount = discountValue;
          bestPromotion = promotion;
        }
      }
    }
  }

  return {
    finalPrice: Math.max(0, originalPrice * quantity - bestDiscount),
    discount: bestDiscount,
    appliedPromotion: bestPromotion,
  };
};

export type { ProductPromotionResult } from "@lemmonhq/types";
/**
 * Calculate all promotions and their effects for a product
 */
export const calculateProductPromotions = (
  services: Service[],
  productId: number,
  originalPrice: number,
  quantity: number = 1,
  allPromotions: Promotion[],
  serviceMethod?: "takeaway" | "dine-in" | "delivery",
): ProductPromotionResult => {
  const applicablePromotions = getPromotionsForProduct(
    services,
    productId,
    allPromotions,
    serviceMethod,
  );

  const discountPromotions = applicablePromotions.filter(
    (p) =>
      p.reward.type === "product_discount" || p.reward.type === "special_price",
  );

  const nonDiscountPromotions = applicablePromotions.filter(
    (p) =>
      p.reward.type !== "product_discount" && p.reward.type !== "special_price",
  );

  let totalDiscount = 0;

  // Calculate total discount from all applicable discount promotions
  for (const promotion of discountPromotions) {
    if (promotion.reward.type === "product_discount") {
      const totalPrice = originalPrice * quantity;
      const discountValue = calculateDiscountValueForItem(
        promotion.reward,
        totalPrice,
        quantity,
      );
      totalDiscount += discountValue;
    } else if (promotion.reward.type === "special_price") {
      const specialPrice = promotion.reward.prices[productId];
      if (specialPrice !== undefined) {
        const totalPrice = originalPrice * quantity;
        const specialPriceTotal = specialPrice * quantity;
        const discountValue = Math.max(0, totalPrice - specialPriceTotal);
        totalDiscount += discountValue;
      }
    }
  }

  const finalPrice = Math.max(0, originalPrice * quantity - totalDiscount);

  return {
    hasDiscountPromotions: discountPromotions.length > 0,
    totalDiscount,
    finalPrice,
    originalPrice: originalPrice * quantity,
    nonDiscountPromotions,
    discountPromotions,
  };
};

// Re-export everything from the utility modules
export {
  formatPromotionDescription,
  getPromotionBadgeText,
  getRequirementText,
  getRewardText,
} from "./display-utils";
export {
  getCheckoutItems,
  getItemProductId,
  getItemQuantity,
  getProductIdsFromCategories,
} from "./item-utils";
export { doesPromotionApplyToProduct } from "./promotion-utils";
export { validateAllRequirements } from "./requirements-validation";
export {
  calculateDiscountValueForItem,
  calculatePromotionApplications,
} from "./reward-calculation";
export {
  getCurrentTimeContext,
  isPromotionActiveNow,
  isPromotionEligible,
} from "./time-utils";
